import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import BottomTabBar from './components/BottomTabBar/BottomTabBar';
import AppRouter from './components/AppRouter/AppRouter';
import PageContainer from './components/PageContainer/PageContainer';
import GameDetailsPage from './pages/GameDetailsPage';
import GamePlayPage from './pages/GamePlayPage';
import AuthGate from './components/AuthGate/AuthGate';
import PaymentResultPage from './pages/PaymentResultPage';
import TestDevicePage from './pages/TestDevicePage';
import MyNotesPage from './pages/MyNotesPage';
import SavedPage from './pages/SavedPage';
import RedirectPage from './pages/RedirectPage';
import SharedGamePage from './pages/SharedGamePage';

import { useAppState } from './hooks/useAppState';
import { useSendRegUtm } from './hooks/useSendRegUtm';
import { useLogAppOpen } from './hooks/useLogAppOpen';
import { ensureUserGender } from './utils/genderUtils';
import { storeRegUtmOnceFromLocation } from './utils/regUtmStorage';
import { APP_STATES } from './constants';
import { getEncryptionKey } from './utils/encryption';
import './styles/tokens.css';
import './styles/base.css';
import './styles/pwa.css';
import './styles/ios-pwa.css';
import './styles/force-fix-yellow.css';
import './styles/safari-ios-fix.css';
import './styles/android-pwa-fix.css';

export default function App() {
  const [selectedGame, setSelectedGame] = useState(null);

  // Используем хук для управления состоянием приложения
  const { appState, session, setAppState } = useAppState();

  // Сохраняем UTM-метки при первом входе в приложение
  useEffect(() => {
    storeRegUtmOnceFromLocation();
  }, []);

  // Отправляем reg_utm_query в RPC после успешного логина
  useSendRegUtm(session?.user?.id);

  // Логируем событие app_open при монтировании приложения
  useLogAppOpen(session?.user?.id, window.location.pathname);

  useEffect(() => {
    if (appState === APP_STATES.MAIN && session?.user) {
      ensureUserGender();
      // Инициализируем ключ шифрования
      getEncryptionKey().catch(err => console.error('Failed to init encryption key:', err));
    }
  }, [appState, session]);

  // Логика управления состоянием теперь находится в хуке useAppState

  // Обработчики PWA
  const handlePWAInstall = () => {
    setAppState(APP_STATES.LOGIN);
  };

  const handlePWASkip = () => {
    setAppState(APP_STATES.LOGIN);
  };

  // Обработчик PWA установки из профиля
  const handlePWAInstallFromProfile = () => {
    setAppState(APP_STATES.PWA_INSTALL_FROM_PROFILE);
  };

  const handlePWASkipFromProfile = () => {
    // Возвращаемся на главную страницу
    setAppState(APP_STATES.MAIN);
  };

  const handleLoginComplete = () => {
    // После успешного входа через Telegram сразу переходим к основному приложению
    // Сессия уже установлена через Telegram login
    setAppState(APP_STATES.MAIN);
  };

  // Функция для сброса состояния (может пригодиться для тестирования)
  const resetAppState = () => {
    // Сначала показываем загрузку, затем проверяем состояние заново
    setAppState(APP_STATES.LOADING);
    setTimeout(() => {
      // Просто переходим к логину, так как PWA уже установлено
      setAppState(APP_STATES.LOGIN);
    }, 100);
  };

  // Обработчики для MainApp
  const handleCloseGame = () => {
    setSelectedGame(null);
  };

  const handleOpenGame = (game) => {
    setSelectedGame(game);
  };


  const handleShare = () => { };

  const handlePlay = () => {
    // Переход к игре будет обрабатываться в MarketPage
  };

  // Компонент-обертка для проверки пути
  const AppWrapper = () => {
    const location = useLocation();

    // Тестовая страница доступна всегда, независимо от состояния приложения
    if (location.pathname === '/test-device') {
      return <TestDevicePage />;
    }

    // Страницы редиректа доступны всегда, независимо от авторизации
    if (location.pathname.startsWith('/ref/') || location.pathname.startsWith('/via-')) {
      return (
        <Routes>
          <Route path="/ref/:slug" element={<RedirectPage />} />
          <Route path="/via-:slug" element={<RedirectPage />} />
        </Routes>
      );
    }

    // Публичная страница игры для шаринга - доступна без авторизации
    if (location.pathname.startsWith('/game/share/')) {
      return (
        <Routes>
          <Route path="/game/share/:slug" element={<SharedGamePage />} />
        </Routes>
      );
    }

    // Для всех остальных страниц используем стандартную логику
    if (appState !== APP_STATES.MAIN) {
      return (
        <AppRouter
          appState={appState}
          session={session}
          onPWAInstall={handlePWAInstall}
          onPWASkip={handlePWASkip}
          onPWAInstallFromProfile={handlePWAInstallFromProfile}
          onPWASkipFromProfile={handlePWASkipFromProfile}
          onLoginComplete={handleLoginComplete}
        />
      );
    }

    // Если appState === MAIN, Routes обработает маршруты ниже
    return null;
  };

  // Компонент для условного рендеринга Routes (проверяем путь внутри)
  const PathAwareRoutes = () => {
    const location = useLocation();

    // Если это тестовая страница, не рендерим Routes (она уже обработана в AppWrapper)
    if (location.pathname === '/test-device') {
      return null;
    }

    // Страницы редиректа тоже обработаны в AppWrapper
    if (location.pathname.startsWith('/ref/') || location.pathname.startsWith('/via-')) {
      return null;
    }

    return (
      <Routes>
        {/* Redirect links - обрабатываем переадресацию с красивых ссылок */}
        <Route path="/ref/:slug" element={<RedirectPage />} />
        <Route path="/via-:slug" element={<RedirectPage />} />

        {/* Игровые страницы - они размонтируются при переходе */}
        <Route path="/game/:gameId" element={
          <AuthGate session={session}>
            <GamePlayPage
              game={selectedGame}
              onClose={handleCloseGame}
            />
          </AuthGate>
        } />
        <Route path="/game/:gameId/details" element={
          <AuthGate session={session}>
            <GameDetailsPage
              game={selectedGame}
              onClose={handleCloseGame}
              onShare={handleShare}
              onPlay={handlePlay}
            />
          </AuthGate>
        } />
        <Route path="/payment/result" element={<PaymentResultPage />} />
        <Route path="/payment-result" element={<PaymentResultPage />} />
        <Route path="/my-notes" element={
          <>
            <AuthGate session={session}>
              <MyNotesPage />
            </AuthGate>
            <BottomTabBar active="notes" />
          </>
        } />
        <Route path="/saved" element={
          <>
            <AuthGate session={session}>
              <SavedPage />
            </AuthGate>
            <BottomTabBar active="bookmark" />
          </>
        } />

        {/* Основные страницы - все рендерятся одновременно, показывается только активная */}
        <Route path="/marketplace" element={
          <>
            <PageContainer
              session={session}
              onResetAppState={resetAppState}
              onPWAInstallFromProfile={handlePWAInstallFromProfile}
              onPWASkipFromProfile={handlePWASkipFromProfile}
              onOpenGame={handleOpenGame}
            />
            <BottomTabBar active="bookmark" />
          </>
        } />
        <Route path="/marketplace/:slug" element={
          <>
            <PageContainer
              session={session}
              onResetAppState={resetAppState}
              onPWAInstallFromProfile={handlePWAInstallFromProfile}
              onPWASkipFromProfile={handlePWASkipFromProfile}
              onOpenGame={handleOpenGame}
            />
            <BottomTabBar active="bookmark" />
          </>
        } />
        <Route path="/home" element={
          <>
            <PageContainer
              session={session}
              onResetAppState={resetAppState}
              onPWAInstallFromProfile={handlePWAInstallFromProfile}
              onPWASkipFromProfile={handlePWASkipFromProfile}
              onOpenGame={handleOpenGame}
            />
            <BottomTabBar active="home" />
          </>
        } />
        <Route path="/home/:slug" element={
          <>
            <PageContainer
              session={session}
              onResetAppState={resetAppState}
              onPWAInstallFromProfile={handlePWAInstallFromProfile}
              onPWASkipFromProfile={handlePWASkipFromProfile}
              onOpenGame={handleOpenGame}
            />
            <BottomTabBar active="home" />
          </>
        } />
        {/* Backward compatibility - redirect old /my-games routes to /home */}
        <Route path="/my-games" element={<Navigate to="/home" replace />} />
        <Route path="/my-games/:slug" element={<Navigate to="/home" replace />} />
        <Route path="/profile" element={
          <>
            <PageContainer
              session={session}
              onResetAppState={resetAppState}
              onPWAInstallFromProfile={handlePWAInstallFromProfile}
              onPWASkipFromProfile={handlePWASkipFromProfile}
              onOpenGame={handleOpenGame}
            />
            <BottomTabBar active="profile" />
          </>
        } />

        {/* Правовая информация - без BottomTabBar */}
        <Route path="/legal-info" element={
          <PageContainer
            session={session}
            onResetAppState={resetAppState}
            onPWAInstallFromProfile={handlePWAInstallFromProfile}
            onPWASkipFromProfile={handlePWASkipFromProfile}
            onOpenGame={handleOpenGame}
          />
        } />

        {/* Редирект по умолчанию */}
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    );
  };

  // Рендер в зависимости от состояния приложения
  return (
    <Router>
      <div className="app-root">
        {/* Проверяем путь и состояние приложения */}
        <AppWrapper />

        {/* Основные роуты показываются только если appState === MAIN и путь не /test-device */}
        {appState === APP_STATES.MAIN && (
          <PathAwareRoutes />
        )}
      </div>
    </Router>
  );
}



